import { RequirementTab, RequirementStatus } from '@/types';

// ============================================================================
// TAB CONFIGURATION
// ============================================================================

/**
 * Tab configuration for Compliance Requirements page
 * Uses API scope values directly (no mapping needed)
 */
export const REQUIREMENT_TABS: { value: RequirementTab; label: string }[] = [
  { value: 'EverDriven', label: 'EverDriven' },
  { value: 'State', label: 'States' },
  { value: 'District', label: 'Districts' },
] as const;

/**
 * Tab value constants for comparison
 * Now matches API scope values directly
 */
export const TAB_VALUES = {
  EVERDRIVEN: 'EverDriven' as const,
  STATE: 'State' as const,
  DISTRICT: 'District' as const,
} as const;

// ============================================================================
// REQUIREMENT TYPES
// ============================================================================

/**
 * Requirement type constants matching API lookup table response
 * These match the exact values from types[].typeName in the API
 */
export const REQUIREMENT_TYPES = {
  DIGITAL_DOCUMENTATION: 'Digital Document/Attestation' as const,
  DOCUMENT_UPLOAD: 'Document Upload' as const,
  DIGITAL_FORM: 'Digital Form' as const,
  INTEGRATION: 'Integration' as const,
} as const;

/**
 * Retired requirement types. These are never offered for creation, but existing
 * records still have to be listed and viewed, so the names stay addressable here
 * rather than being repeated as literals across the UI.
 */
export const DEPRECATED_REQUIREMENT_TYPES = {
  AUTOMATED_APPROVAL: 'Automated Approval' as const,
} as const;

/**
 * Normalized requirement type strings for comparison
 * Used in UI logic for type checking
 */
export const NORMALIZED_REQUIREMENT_TYPES = {
  DIGITAL_DOCUMENTATION: 'digital document/attestation' as const,
  DOCUMENT_UPLOAD: 'document upload' as const,
  DIGITAL_FORM: 'digital form' as const,
  INTEGRATION: 'integration' as const,
} as const;

// ============================================================================
// DOCUMENT TYPE OPTIONS
// ============================================================================

/**
 * Document type options for Document Upload requirements
 * These match the exact values from documentTypes[].name in the API lookup table
 */
export const DOCUMENT_TYPES = {
  USER_PROVIDED: 'User-Provided' as const,
  EVERDRIVEN_TEMPLATE: 'EverDriven Template' as const,
  LINK: 'Link' as const,
} as const;

/**
 * Normalized document type strings for comparison
 * Use these values when performing case-insensitive string comparisons in the UI or business logic.
 * These preserve consistent lowercase forms for internal logic.
 */
export const NORMALIZED_DOCUMENT_TYPES = {
  USER_PROVIDED: 'user-provided' as const,
  EVERDRIVEN_TEMPLATE: 'everdriven-template' as const,
  LINK: 'link' as const,
} as const;

/**
 * Allowed file types for template uploads
 */
export const TEMPLATE_ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

// ============================================================================
// ACKNOWLEDGEMENT FORMAT OPTIONS
// ============================================================================

/**
 * Acknowledgement format options for Digital Documentation requirements
 * These match the API lookup table: acknowledgementFormats[].acknowledgementFormatName
 */
export const ACKNOWLEDGEMENT_FORMATS = {
  DIGITAL_SIGNATURE: 'Digital Signature' as const,
  DIGITAL_INITIALS: 'Digital Initials' as const,
  CHECKBOX: 'Checkbox' as const,
} as const;

export const ACKNOWLEDGEMENT_FORMAT_OPTIONS = [
  { value: ACKNOWLEDGEMENT_FORMATS.DIGITAL_SIGNATURE, label: 'Digital Signature' },
  { value: ACKNOWLEDGEMENT_FORMATS.DIGITAL_INITIALS, label: 'Digital Initials' },
  { value: ACKNOWLEDGEMENT_FORMATS.CHECKBOX, label: 'Checkbox' },
] as const;

export const ACKNOWLEDGEMENT_FORMAT_LABELS: Record<string, string> = {
  [ACKNOWLEDGEMENT_FORMATS.CHECKBOX]: 'Checkbox',
  [ACKNOWLEDGEMENT_FORMATS.DIGITAL_SIGNATURE]: 'Digital Signature',
  [ACKNOWLEDGEMENT_FORMATS.DIGITAL_INITIALS]: 'Digital Initials',
};

// ============================================================================
// PROCESSING METHOD OPTIONS
// ============================================================================

/**
 * Processing method names from the API lookup table: processingMethods[].processingMethodName
 * Only used to type the processing method carried by legacy Automated Approval records —
 * that type can no longer be created, so there are no selectable options for it.
 */
export const PROCESSING_METHODS = {
  MANUAL_REVIEW: 'Manual Review' as const,
  AUTOMATIC: 'Automatic' as const,
  WORKFLOW_BASED: 'Workflow Based' as const,
} as const;

// ============================================================================
// NOTIFICATION FREQUENCY OPTIONS
// ============================================================================

/**
 * Notification frequency constants for requirement notifications
 * These match the values from the lookup table API response
 */
export const NOTIFICATION_FREQUENCIES = {
  DAILY: 'Daily' as const,
  WEEKLY: 'Weekly' as const,
} as const;

/**
 * Notification type constants for requirement notifications
 * These match the values from the lookup table API response
 */
export const NOTIFICATION_TYPES = {
  IN_APP: 'In-App' as const,
  EMAIL: 'Email' as const,
} as const;


/**
 * Notification frequency options for dropdown
 * Matches API response: notificationFrequencies[].notificationFrequencyName
 */
export const NOTIFICATION_FREQUENCY_OPTIONS = [
  { value: NOTIFICATION_FREQUENCIES.DAILY, label: 'Daily' },
  { value: NOTIFICATION_FREQUENCIES.WEEKLY, label: 'Weekly' },
] as const;

// ============================================================================
// EXPIRATION CONFIGURATION
// ============================================================================

/**
 * Expiration type constants for requirement expiration logic
 * These match the API lookup table: expirationTypes[].expirationTypeName
 */
export const EXPIRATION_TYPES = {
  EVERDRIVEN_DEFINED: 'EverDriven Defined' as const,
  DOCUMENT_DATE: 'Document Date' as const,
  APPROVAL_DATE: 'Approval Date' as const,
} as const;

/**
 * Predefined expiration time periods for requirement expiration logic
 * These match the API lookup table: predefinedExpirationTimePeriods[].Name
 */
export const PREDEFINED_EXPIRATION_TIME_PERIODS = {
  MONTHS_12: '12 Months' as const,
  MONTHS_24: '24 Months' as const,
  MONTHS_36: '36 Months' as const,
} as const;


/**
 * Time period constants for expiration duration
 * These match the API lookup table: timePeriods[].timePeriodName
 */
export const TIME_PERIODS = {
  DAYS: 'Days' as const,
  WEEKS: 'Weeks' as const,
  MONTHS: 'Months' as const,
  YEARS: 'Years' as const,
} as const;

// ============================================================================
// APPROVAL CONFIGURATION
// ============================================================================

/**
 * Approval type constants for requirement approval logic
 * Backend values remain: Manual, Automated, None
 * Frontend displays: Approval Required (Manual), No Approval Required (None)
 * Automated is reserved for Integration type requirements
 */
export const APPROVAL_TYPES = {
  NO: 'None' as const,
  APPROVAL_REQUIRED: 'Manual' as const,
  AUTO: 'Automated' as const,
} as const;

/**
 * Display labels for the backend approval type values. Any value without an entry
 * here (or added later) falls back to the backend name.
 */
export const APPROVAL_TYPE_LABELS: Record<string, string> = {
  [APPROVAL_TYPES.APPROVAL_REQUIRED]: 'Approval Required',
  [APPROVAL_TYPES.NO]: 'No Approval Required',
  [APPROVAL_TYPES.AUTO]: 'Automated',
};

// ============================================================================
// INTEGRATION CONFIGURATION
// ============================================================================

/**
 * Integration service constants, used to map a service to the roles it applies to.
 * Providers and services themselves come from the lookup tables, not from here.
 */
export const INTEGRATION_SERVICES = {
  BACKGROUND_CHECK: 'Background Check' as const,
  MVR: 'MVR' as const,
} as const;

/**
 * Integration provider constants. Named here only where a provider drives behaviour the
 * generic Integration flow doesn't cover — currently just the prerequisite gate, which is
 * specific to Checkr's hosted verification.
 */
export const INTEGRATION_PROVIDERS = {
  CHECKR: 'Checkr' as const,
} as const;

/**
 * Role mappings for Integration services.
 * Selecting a service auto-populates (and locks) the requirement's Applies To roles.
 * A service with no entry here leaves Applies To editable so a new provider/service can
 * be configured without a code change.
 */
export const INTEGRATION_SERVICE_ROLES: Record<string, string[]> = {
  [INTEGRATION_SERVICES.BACKGROUND_CHECK]: ['Drivers', 'Monitors'],
  [INTEGRATION_SERVICES.MVR]: ['Drivers'],
};

// ============================================================================
// STATUS CONFIGURATION
// ============================================================================

/**
 * Status constants for requirement status comparison
 * These match the API lookup table: statuses[].statusName
 */
export const STATUS_VALUES = {
  DRAFT: 'Draft' as const,
  ACTIVE: 'Active' as const,
  INACTIVE: 'Inactive' as const,
  ARCHIVED: 'Archived' as const,
  READY_TO_PUBLISH: 'Ready to Publish' as const,
} as const;

/**
 * Status badge configuration
 * Maps requirement status to display properties
 */
export const STATUS_CONFIG: Record<
  RequirementStatus,
  { color: 'success' | 'warning' | 'info' | 'default'; label: string }
> = {
  Draft: { color: 'info', label: 'Draft' },
  Active: { color: 'success', label: 'Active' },
  Inactive: { color: 'default', label: 'Disabled' },
  Archived: { color: 'default', label: 'Archived' },
  'Ready to Publish': { color: 'warning', label: 'Ready to Publish' },
} as const;

// ============================================================================
// FILTER DEFAULTS
// ============================================================================

/**
 * Default filter values for dropdowns
 */
export const FILTER_DEFAULTS = {
  ALL_STATES: 'All States' as const,
  ALL_DISTRICTS: 'All Districts' as const,
} as const;

// ============================================================================
// SORT CONFIGURATION
// ============================================================================

/**
 * Sort order constants
 */
export const SORT_ORDER = {
  ASC: 'asc' as const,
  DESC: 'desc' as const,
} as const;

/**
 * Sortable field names
 */
export const SORT_FIELDS = {
  NAME: 'name' as const,
  TYPE: 'type' as const,
  STATUS: 'status' as const,
  APPLIES_TO: 'appliesTo' as const,
  REQUIRED: 'required' as const,
  SHARED: 'shared' as const,
  COMPLIANCE_IMPACTING: 'complianceImpacting' as const,
} as const;

// ============================================================================
// DECISION VALUES
// ============================================================================

/**
 * Yes/No decision values for form radio buttons
 */
export const DECISION_VALUES = {
  YES: 'yes' as const,
  NO: 'no' as const,
} as const;

// ============================================================================
// DEFAULT EXPANDED ITEMS
// ============================================================================

/**
 * Default expanded state/district names in accordion views
 */
export const DEFAULT_EXPANDED = {
  STATE: 'California' as const,
  DISTRICT: 'Downtown Financial District' as const,
} as const;

// ============================================================================
// VALIDATION MESSAGES
// ============================================================================

/**
 * Validation error messages for form fields
 */
export const VALIDATION_MESSAGES = {
  REQUIRED_FIELD: 'This field is required',
  SELECT_AT_LEAST_ONE_APPLIES_TO: 'Please select at least one "Applies To" option',
  SELECT_EXPIRATION_DURATION: 'Please enter an expiration duration greater than 0',
  SELECT_EXPIRATION_DATE: 'Please select an expiration date',
  SELECT_APPROVING_ROLES: 'Please select at least one approving role',
  SELECT_APPROVING_INTEGRATION: 'Please select an approving integration',
  SELECT_STATES: 'Please select at least one state',
  SELECT_DISTRICTS: 'Please select at least one district',
  INVALID_SCOPE: (tab: string, availableScopes: string[]) =>
    `Invalid scope for current tab "${tab}". Expected one of: ${availableScopes.join(', ')}.`,
} as const;

// ============================================================================
// SCOPE MAPPINGS (Imported from config but duplicated for convenience)
// ============================================================================

/**
 * Scope name mappings between frontend tabs and backend API
 * These match the API lookup table: scopes[].scopeName
 * Note: Frontend uses plural (states, districts) but API uses singular (State, District)
 */
export const SCOPE_NAMES = {
  EVERDRIVEN: 'EverDriven' as const,
  STATE: 'State' as const,
  DISTRICT: 'District' as const,
} as const;

// ============================================================================
// DIALOG STEP INDICES
// ============================================================================

/**
 * Step indices for the Add/Edit Requirement Dialog stepper
 */
export const DIALOG_STEPS = {
  SCOPE_SELECTION: 0,
  BASIC_SETTINGS: 1,
  REQUIREMENT_DETAILS: 2,
  REVIEW: 3,
} as const;

/**
 * Step labels for the dialog stepper
 */
export const DIALOG_STEP_LABELS = [
  'Scope',
  'Basic Settings',
  'Requirement Details',
  'Review',
] as const;

// File upload validation constants
export const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
export const MAX_FILE_SIZE_MB = 5;

export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

export const ALLOWED_FILE_EXTENSIONS = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp';

// ============================================================================
// TYPE UNIONS FROM CONSTANTS
// ============================================================================

/**
 * Type unions derived from constants for type-safe usage across the application
 * These ensure that form values, comparisons, and API transformations use consistent values
 */

export type ExpirationTypeValue = typeof EXPIRATION_TYPES[keyof typeof EXPIRATION_TYPES];
export type TimePeriodValue = typeof TIME_PERIODS[keyof typeof TIME_PERIODS];
export type PredefinedExpirationTimePeriodValue = typeof PREDEFINED_EXPIRATION_TIME_PERIODS[keyof typeof PREDEFINED_EXPIRATION_TIME_PERIODS];
export type ApprovalTypeValue = typeof APPROVAL_TYPES[keyof typeof APPROVAL_TYPES];
export type AcknowledgementFormatValue = typeof ACKNOWLEDGEMENT_FORMATS[keyof typeof ACKNOWLEDGEMENT_FORMATS];
export type ProcessingMethodValue = typeof PROCESSING_METHODS[keyof typeof PROCESSING_METHODS];
export type NotificationFrequencyValue = typeof NOTIFICATION_FREQUENCIES[keyof typeof NOTIFICATION_FREQUENCIES];
export type StatusValue = typeof STATUS_VALUES[keyof typeof STATUS_VALUES];
export type ScopeNameValue = typeof SCOPE_NAMES[keyof typeof SCOPE_NAMES];

// ============================================================================
// APPLIES TO OPTIONS
// ============================================================================

/**
 * Static Applies To options defining display order and parent/child grouping.
 * Values must exactly match what the backend returns so they are stored correctly.
 * "All Vehicles" is a parent that auto-selects its children when checked.
 */
export interface AppliesToOption {
  label: string;
  children?: string[];
  parent?: string;
}

export const APPLIES_TO_OPTIONS: AppliesToOption[] = [
  { label: 'Service Providers' },
  { label: 'Drivers' },
  { label: 'Monitors' },
  { label: 'All Vehicles', children: ['Personal Vehicles', 'Fleet Vehicles', 'Rental Vehicles'] },
  { label: 'Personal Vehicles', parent: 'All Vehicles' },
  { label: 'Fleet Vehicles', parent: 'All Vehicles' },
  { label: 'Rental Vehicles', parent: 'All Vehicles' },
];

// ============================================================================
// DIGITAL FORM FIELD TYPES
// ============================================================================

/**
 * Field type constants for Digital Form requirements
 * These match the API lookup table: formFieldTypes[].typeName
 */
export const FORM_FIELD_TYPES = {
  SINGLE_LINE: 'Single Line' as const,
  LONG_TEXT: 'Long Text' as const,
  RADIO_GROUP: 'Radio Group' as const,
  CHECK_GROUP: 'Check Group' as const,
  DROPDOWN: 'Dropdown' as const,
  SPECIALITY: 'Speciality' as const,
  SECTION_TITLE: 'Section Title' as const,
} as const;

export type FormFieldTypeValue = typeof FORM_FIELD_TYPES[keyof typeof FORM_FIELD_TYPES];

/**
 * Sub-type names for Single Line fields.
 * These match the API lookup table: formFieldTypes[Single Line].subTypes[].name
 */
export const FORM_FIELD_SUBTYPES = {
  EXPIRATION_DATE: 'Expiration Date' as const,
} as const;
